import { Component, inject, OnInit, ViewChild } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { BreadcrumbComponent } from '../../../../shared/components/breadcrumb/breadcrumb.component';
import { IBreadcrumbConfig } from '@interfaces/breadcrumb-config';
import { CommonModule } from '@angular/common';
import { MatStepper, MatStepperModule } from '@angular/material/stepper';
import { MatOption, MatSelect } from '@angular/material/select';
import { Router } from '@angular/router';
import { ModalClientComponent } from '../../components/modal-client/modal-client.component';
import { InsuranceService } from 'src/app/core/services/insurance.service';
import { DataService } from 'src/app/core/services/data.service';
import { ICobertura } from '@interfaces/cobertura.interface';
import { ITipoExpediente } from '@interfaces/tipo-expediente.interface';
import { ISiniestroRequest } from '@interfaces/siniestro-request.interface';
import { MatRadioChange } from '@angular/material/radio';

@Component({
  selector: 'aig-new-claim',
  standalone: true,
  imports: [
    BreadcrumbComponent,
    ReactiveFormsModule,
    CommonModule,
    MatStepperModule,
    MatSelect,
    MatOption,
    ModalClientComponent,
  ],
  templateUrl: './new-claim.component.html',
  styleUrls: ['./new-claim.component.scss'],
})
export class NewClaimComponent implements OnInit {
  @ViewChild('stepper') stepper!: MatStepper;
  // STEPPER
  isLinear = true;
  public personalDataForm: FormGroup = new FormGroup({});
  public TypeSinisterDataForm: FormGroup = new FormGroup({});
  public DataSinisterDataForm: FormGroup = new FormGroup({});
  public DocumentationDataForm: FormGroup = new FormGroup({});
  public OKDataForm: FormGroup = new FormGroup({});

  public visibleClientModal: boolean = false;

  uploadedFiles: File[] = [];
  uploadError: string | null = null;
  idPoliza!: number;

  private fb = inject(FormBuilder);

  public tipoSiniestroOptions: ITipoExpediente[] = [];

  public tipoCoberturaOptions: ICobertura[] = [];

  public esTitular: boolean = false;

  constructor(
    private insuranceService: InsuranceService,
    private dataService: DataService,
    private router: Router
  ) {}
  ngOnInit(): void {
    this.idPoliza = this.dataService.getPoliza();

    if (!this.idPoliza || this.idPoliza === 0) {
      // 🚨 Redirigir si idPoliza no es válido
      this.router.navigate(['/area-cliente']); // <-- ajusta esta ruta
      return;
    }

    this.visibleClientModal = true;

    this.personalDataForm = this.fb.group({
      nombre: ['', Validators.required],
      dni: ['', Validators.required],
      telefono: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      direccion: ['', Validators.required],
      cp: ['', Validators.required],
      poblacion: ['', Validators.required],
      provincia: ['', Validators.required],
    });

    this.TypeSinisterDataForm = this.fb.group({
      tipoSiniestro: ['', Validators.required],
      tipoCobertura: [{ value: '', disabled: true }, Validators.required],
    });

    //Carga inicial de tipos de siniestros
    this.insuranceService.obtenerTiposSiniestro().subscribe((tipos) => {
      this.tipoSiniestroOptions = tipos;
    });

    this.TypeSinisterDataForm.get('tipoSiniestro')!.valueChanges.subscribe(
      (selectedTipo: ITipoExpediente | number | null) => {
        // Limpia las coberturas
        this.tipoCoberturaOptions = [];
        const coberturaControl = this.TypeSinisterDataForm.get('tipoCobertura');
        coberturaControl!.setValue([]);

        //Si hay tipo seleccionado, carga coberturas
        let idTipo: number | undefined;
        if (
          selectedTipo &&
          typeof selectedTipo === 'object' &&
          'idTipoExpediente' in selectedTipo
        ) {
          idTipo = selectedTipo.idTipoExpediente;
        } else if (typeof selectedTipo === 'number') {
          idTipo = selectedTipo;
        } else {
          idTipo = undefined;
        }

        if (idTipo) {
          coberturaControl?.enable();
          this.insuranceService
            .obtenerCoberturas(idTipo)
            .subscribe((coberturas) => {
              this.tipoCoberturaOptions = coberturas;
            });
        } else {
          coberturaControl?.disable();
        }
      }
    );

    this.DataSinisterDataForm = this.fb.group({
      descripcion: ['', Validators.required],
      lugar: ['', Validators.required],
      fecha: ['', Validators.required],
      ciudad: ['', Validators.required],
      pais: ['', Validators.required],
      dispositivo: [''],
      marca: [''],
      modelo: [''],
      nserie: [''],
    });

    this.DocumentationDataForm = this.fb.group({
      documentos: [[], Validators.required],
    });
  }

  onTitularAnswered(esTitular: boolean) {
    this.esTitular = esTitular;
    this.visibleClientModal = false;
    if (esTitular) {
      // Llama al servicio y rellena el formulario
      this.insuranceService
        .obtenerDatosTitularPoliza(this.idPoliza)
        .subscribe((datos) => {
          // Aquí rellenas tu formulario, por ejemplo:
          this.personalDataForm.patchValue({
            nombre: datos.titular,
            dni: datos.numDocumento,
            telefono: datos.telefono,
            email: datos.email,
            direccion: datos.direccion,
            cp: datos.codigoPostal,
            poblacion: datos.poblacion,
            provincia: datos.provincia,
          });
        });
    } else {
      // El usuario no es el titular, quizás limpias el formulario o haces otra acción
      this.personalDataForm.reset();
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = input.files;

    if (files && files.length) {
      const filesArray = Array.from(files);
      this.uploadedFiles = [...this.uploadedFiles, ...filesArray];
      // Guardar en el form
      this.DocumentationDataForm.get('documentos')!.setValue(
        this.uploadedFiles
      );
      this.DocumentationDataForm.get('documentos')!.markAsDirty();
    }

    // 🔑 Permite que seleccionar los mismos ficheros vuelva a disparar (change)
    input.value = '';
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      const filesArray = Array.from(files);
      this.uploadedFiles = [...this.uploadedFiles, ...filesArray];
      this.uploadError = null;
      this.DocumentationDataForm.get('documentos')!.setValue(
        this.uploadedFiles
      );
      this.DocumentationDataForm.get('documentos')!.markAsDirty();
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
  }

  handleFile(file: File): void {
    // Simular error si el archivo tiene nombre 'error'
    if (file.name === 'error.pdf') {
      this.uploadError = file.name;
    } else {
      this.uploadedFiles.push(file);
      this.uploadError = null;
    }
  }

  removeFile(file: File): void {
    this.uploadedFiles = this.uploadedFiles.filter((f) => f !== file);
    if (file.name === this.uploadError) this.uploadError = null;
    this.DocumentationDataForm.get('documentos')!.setValue(this.uploadedFiles);
    this.DocumentationDataForm.get('documentos')!.markAsDirty();
  }
  // FormBuilder

  public Comeback(): void {
    this.router.navigate(['/area-cliente']);
  }

  onFinalizarStepper() {
    if (
      this.personalDataForm.valid &&
      this.TypeSinisterDataForm.valid &&
      this.DataSinisterDataForm.valid &&
      this.DocumentationDataForm.valid
    ) {
      const request: ISiniestroRequest = {
        idPoliza: this.idPoliza,
        puedeAbrirSiniestro: true,
        siniestroCurso: false,
        estadoPoliza: 1,
        tipoPersonaApertura: this.esTitular ? 0 : 2,
        nombreCliente: this.personalDataForm.get('nombre')?.value.toString(),
        numeroDocumento: this.personalDataForm.get('dni')?.value.toString(),
        telefonoContacto1: this.personalDataForm
          .get('telefono')
          ?.value.toString(),
        email: this.personalDataForm.get('email')?.value.toString(),
        direccionEntrega: this.personalDataForm
          .get('direccion')
          ?.value.toString(),
        codigoPostal: this.personalDataForm.get('cp')?.value.toString(),
        poblacion: this.personalDataForm.get('poblacion')?.value.toString(),
        provincia: this.personalDataForm.get('provincia')?.value.toString(),
        tipoSiniestro: this.TypeSinisterDataForm.get('tipoSiniestro')?.value,
        coberturas: this.TypeSinisterDataForm.get('tipoCobertura')?.value,
        comoSeProdujoSiniestro:
          this.DataSinisterDataForm.get('descripcion')?.value.toString(),
        lugarSuceso: this.DataSinisterDataForm.get('lugar')?.value.toString(),
        fechaSuceso: this.DataSinisterDataForm.get('fecha')?.value.toString(),
        ciudadSuceso: this.DataSinisterDataForm.get('ciudad')?.value.toString(),
        paisSuceso: this.DataSinisterDataForm.get('pais')?.value.toString(),
        tipoDispositivo:
          this.DataSinisterDataForm.get('dispositivo')?.value.toString(),
        marca: this.DataSinisterDataForm.get('marca')?.value.toString(),
        modelo: this.DataSinisterDataForm.get('modelo')?.value.toString(),
        numSerie: this.DataSinisterDataForm.get('nserie')?.value.toString(),
        documentos: this.DocumentationDataForm.get('documentos')?.value,
      };

      this.insuranceService.aperturaSiniestro(request).subscribe({
        next: (response) => {
          // this.isloading = false;
          this.stepper.next(); // avanza al último paso de confirmación
        },
        error: (err) => {
          // this.isloading = false;
          // this.submiterror = 'ha ocurrido un error, por favor revisa los datos e inténtalo de nuevo.';
          // puedes mostrar el error en el template
        },
      });
    }
  }
}
